import * as z from "zod";

import {
  TABLE_ACCESSOR_ASSIGNED_TO,
  TABLE_ACCESSOR_CHANNEL,
  TABLE_ACCESSOR_CONTACT,
  TABLE_ACCESSOR_HAS_PENDING_SCHEDULE_ACTIONS,
  TABLE_ACCESSOR_IS_DRAFT,
  TABLE_ACCESSOR_ORDER_FLOW,
  TABLE_ACCESSOR_PREVIEW_TEXT,
  TABLE_ACCESSOR_SALE_ID,
  TABLE_ACCESSOR_SALE_NAME,
  TABLE_ACCESSOR_STATUS_UPDATED_AT,
  TABLE_ACCESSOR_SUB_STATUS,
  TABLE_ACCESSOR_TITLE,
} from "~/components/common/tickets/ticket-list";
import {
  SalesTeamName,
  SaleType,
  Store,
  SupportTicketChannel,
} from "~/graphql/graphql.gen";
import { flowQueryParamValues } from "~/stores/flows-store";
import { subStatusQueryParamValues } from "~/stores/ticket-sub-statuses-store";
import { dateFilterSchema } from "~/validators/date-filter-validator";

export const STATUS_CHANGED_ASC = "status-changed-asc";
export const STATUS_CHANGED_DESC = "status-changed-desc";

export const GROUP_BY_NONE = "none";
export const GROUP_BY_SALE_ID = TABLE_ACCESSOR_SALE_ID;
export const GROUP_BY_SUB_STATUS = TABLE_ACCESSOR_SUB_STATUS;
export const GROUP_BY_ASSIGNED_TO = TABLE_ACCESSOR_ASSIGNED_TO;

export const DISPLAYED_PROPERTIES_ASSIGNED_TO = TABLE_ACCESSOR_ASSIGNED_TO;
export const DISPLAYED_PROPERTIES_CHANNEL = TABLE_ACCESSOR_CHANNEL;
export const DISPLAYED_PROPERTIES_CONTACT = TABLE_ACCESSOR_CONTACT;
export const DISPLAYED_PROPERTIES_HAS_PENDING_SCHEDULE_ACTIONS =
  TABLE_ACCESSOR_HAS_PENDING_SCHEDULE_ACTIONS;
export const DISPLAYED_PROPERTIES_IS_DRAFT = TABLE_ACCESSOR_IS_DRAFT;
export const DISPLAYED_PROPERTIES_ORDER_FLOW = TABLE_ACCESSOR_ORDER_FLOW;
export const DISPLAYED_PROPERTIES_PREVIEW_TEXT = TABLE_ACCESSOR_PREVIEW_TEXT;
export const DISPLAYED_PROPERTIES_SALE_NAME = TABLE_ACCESSOR_SALE_NAME;
export const DISPLAYED_PROPERTIES_STATUS_UPDATED_AT =
  TABLE_ACCESSOR_STATUS_UPDATED_AT;
export const DISPLAYED_PROPERTIES_SUB_STATUS = TABLE_ACCESSOR_SUB_STATUS;
export const DISPLAYED_PROPERTIES_TITLE = TABLE_ACCESSOR_TITLE;

const groupByQueryParamValues = [
  GROUP_BY_NONE,
  GROUP_BY_SALE_ID,
  GROUP_BY_SUB_STATUS,
  GROUP_BY_ASSIGNED_TO,
] as const;

const sortTicketsQueryParamValues = [
  STATUS_CHANGED_ASC,
  STATUS_CHANGED_DESC,
] as const;

export const DEFAULT_SORT = STATUS_CHANGED_ASC;

export const DEFAULT_GROUP_BY = GROUP_BY_NONE;

export const DEFAULT_DISPLAYED_PROPERTIES = {
  assignedTo: true,
  channel: true,
  contact: true,
  hasPendingScheduleActions: true,
  isDraft: true,
  orderFlow: true,
  previewText: true,
  saleName: false,
  subStatus: true,
  statusUpdatedAt: true,
  title: true,
};

export type DisplayedPropertiesKeys = keyof typeof DEFAULT_DISPLAYED_PROPERTIES;

/** A filter in the URL: its values, and whether to keep or exclude them. */
const queryFilterSchema = <T extends z.ZodType>(value: T) =>
  z.object({ operator: z.enum(["is", "is_not"]), value }).optional();

export const searchTicketsQueryParamsSchema = z.object({
  filters: z
    .object({
      assignedTo: queryFilterSchema(z.string().array()),
      channel: queryFilterSchema(z.enum(SupportTicketChannel).array()),
      contactId: queryFilterSchema(z.string().array()),
      createdAt: dateFilterSchema.optional(),
      flow: queryFilterSchema(z.enum(flowQueryParamValues).array()),
      hasPendingScheduleActions: queryFilterSchema(z.boolean()),
      isDraft: queryFilterSchema(z.boolean()),
      orderId: queryFilterSchema(z.string().array()),
      orderFlowId: queryFilterSchema(z.string().array()),
      saleId: queryFilterSchema(z.string().array()),
      salesTeam: queryFilterSchema(z.enum(SalesTeamName).array()),
      saleType: queryFilterSchema(z.enum(SaleType).array()),
      store: queryFilterSchema(z.enum(Store).array()),
      subStatus: queryFilterSchema(z.enum(subStatusQueryParamValues).array()),
      supplierId: queryFilterSchema(z.string().array()),
    })
    .optional(),
  sort: z.enum(sortTicketsQueryParamValues).catch(DEFAULT_SORT),
  groupBy: z.enum(groupByQueryParamValues).catch(DEFAULT_GROUP_BY),
  displayedProperties: z
    .object({
      assignedTo: z.boolean(),
      channel: z.boolean(),
      contact: z.boolean(),
      hasPendingScheduleActions: z.boolean(),
      isDraft: z.boolean(),
      orderFlow: z.boolean(),
      previewText: z.boolean(),
      saleName: z.boolean(),
      subStatus: z.boolean(),
      statusUpdatedAt: z.boolean(),
      title: z.boolean(),
    })
    .catch(DEFAULT_DISPLAYED_PROPERTIES),
});

export type SearchTicketQueryParams = z.infer<
  typeof searchTicketsQueryParamsSchema
>;
